import {
  ADDITIONAL_OFFSET_RATIO,
  BASE_OFFSET_RATIO,
  GROUP_UNDERLYING_BLOCK_OFFSET_RATIO,
  LAYER_SEPARATION_BONUS,
  MAX_MINUTES_IN_DAY,
  MIN_DURATION,
  MIN_WIDTH,
  MULTI_BLOCK_FALLBACK_BASE_WIDTH_RATIO,
  MULTI_BLOCK_FALLBACK_OFFSET_START,
  MULTI_BLOCK_FALLBACK_OFFSET_STEP,
  MULTI_BLOCK_LAYOUT_CONFIG,
  TIME_THRESHOLD,
  TOTAL_WIDTH_RATIO,
} from './constants';
import type { ProgramBlock } from './types';

export const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const toTimeString = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

export const checkOverlap = (block1: ProgramBlock, block2: ProgramBlock) => {
  if (block1.columnIdx !== block2.columnIdx || block1.id === block2.id)
    return false;

  const start1 = toMinutes(block1.start);
  const start2 = toMinutes(block2.start);
  const end1 = toMinutes(block1.end);
  const end2 = toMinutes(block2.end);

  if (!(start1 < end2 && start2 < end1)) return false;

  const startTimeDiff = Math.abs(start1 - start2);

  return startTimeDiff <= TIME_THRESHOLD;
};

export const checkVisualOverlap = (
  block1: ProgramBlock,
  block2: ProgramBlock
) => {
  if (block1.columnIdx !== block2.columnIdx || block1.id === block2.id)
    return false;

  const start1 = toMinutes(block1.start);
  const start2 = toMinutes(block2.start);
  const end1 = toMinutes(block1.end);
  const end2 = toMinutes(block2.end);

  const visualEnd1 = Math.max(end1, start1 + MIN_DURATION);
  const visualEnd2 = Math.max(end2, start2 + MIN_DURATION);

  return start1 < visualEnd2 && start2 < visualEnd1;
};

export function getBlockStyle(block: ProgramBlock, rowHeight: number) {
  const startMin = toMinutes(block.start);
  const endMin = toMinutes(block.end);
  const duration = endMin - startMin;

  const effectiveDuration = Math.max(duration, MIN_DURATION);

  const height = `${(effectiveDuration / 60) * rowHeight}rem`;

  const minute = block.start.split(':').map(Number)[1];
  const percent = (minute / 60) * 100;
  const offsetTop = `${percent}%`;

  return { top: offsetTop, height };
}

export const checkStartTimeIsBetween = (
  durationStartTime: string,
  startTime: string
) => {
  const durationStartMinutes = toMinutes(durationStartTime);
  const blockStartMinutes = toMinutes(startTime);
  const nextHourMinutes = durationStartMinutes + 60;

  return (
    blockStartMinutes >= durationStartMinutes &&
    blockStartMinutes < nextHourMinutes
  );
};

export const calculateNewTimes = (
  originalBlock: ProgramBlock,
  newStartTime: string,
  dropPositionY: number,
  cellHeight: number,
  snapInterval = 15
) => {
  const originalDuration =
    toMinutes(originalBlock.end) - toMinutes(originalBlock.start);

  const dropTimeInfo = calculateDropTimeOffset(
    newStartTime,
    dropPositionY,
    cellHeight,
    snapInterval
  );

  const clampedTimes = clampTimeWithinBounds(
    dropTimeInfo.newStartMinutes,
    originalDuration
  );

  const newStart = toTimeString(clampedTimes.startMinutes);
  const newEnd = toTimeString(clampedTimes.endMinutes);

  return { newStart, newEnd };
};

const calculateDropTimeOffset = (
  baseTime: string,
  dropY: number,
  cellHeight: number,
  snapInterval: number
) => {
  const baseMinutes = toMinutes(baseTime);
  const offsetMinutes = Math.round((dropY / cellHeight) * 60);
  const alignedOffsetMinutes =
    Math.round(offsetMinutes / snapInterval) * snapInterval;
  const newStartMinutes = baseMinutes + alignedOffsetMinutes;

  return { newStartMinutes };
};

const clampTimeWithinBounds = (startMinutes: number, duration: number) => {
  const clampedStartMinutes = Math.max(
    0,
    Math.min(MAX_MINUTES_IN_DAY - duration, startMinutes)
  );
  const clampedEndMinutes = clampedStartMinutes + duration;

  return { startMinutes: clampedStartMinutes, endMinutes: clampedEndMinutes };
};

const findAllConnectedBlocks = (
  startBlock: ProgramBlock,
  allColumnBlocks: ProgramBlock[]
): ProgramBlock[] => {
  const visited = new Set<string>();
  const queue = [startBlock];
  const result: ProgramBlock[] = [];

  while (queue.length > 0) {
    const currentBlock = queue.shift();

    if (!currentBlock || visited.has(currentBlock.id)) continue;
    visited.add(currentBlock.id);
    result.push(currentBlock);

    const directlyOverlapping = allColumnBlocks.filter(
      (otherBlock) =>
        !visited.has(otherBlock.id) && checkOverlap(currentBlock, otherBlock)
    );

    queue.push(...directlyOverlapping);
  }

  return result;
};

const findUnderlyingBlocks = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[]
): ProgramBlock[] => {
  const blockStart = toMinutes(block.start);

  return allColumnBlocks.filter((otherBlock) => {
    if (otherBlock.id === block.id) return false;
    const otherStart = toMinutes(otherBlock.start);
    return otherStart < blockStart && checkVisualOverlap(block, otherBlock);
  });
};

const calculateSimpleZIndex = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[]
): number => {
  const sameColumnBlocks = allColumnBlocks.filter(
    (b) => b.columnIdx === block.columnIdx
  );

  const sorted = sameColumnBlocks.sort((a, b) => {
    const startDiff = toMinutes(a.start) - toMinutes(b.start);
    if (startDiff !== 0) return startDiff;

    const endDiff = toMinutes(b.end) - toMinutes(a.end);
    if (endDiff !== 0) return endDiff;
    return a.id.localeCompare(b.id);
  });

  return 10 + sorted.findIndex((b) => b.id === block.id);
};

function getLayerSeparationBonus(
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[],
  underlyingBlocks: ProgramBlock[]
) {
  const blockStart = toMinutes(block.start);
  const underlyingOverlappingBlocks = underlyingBlocks.filter((otherBlock) => {
    const otherStart = toMinutes(otherBlock.start);
    return otherStart < blockStart && checkVisualOverlap(block, otherBlock);
  });

  if (underlyingOverlappingBlocks.length > 0) {
    const closestUnderlyingBlock = underlyingBlocks
      .sort((a, b) => toMinutes(b.start) - toMinutes(a.start))
      .find((otherBlock) => checkVisualOverlap(block, otherBlock));

    if (closestUnderlyingBlock) {
      const closestBlockOverlapping = findAllConnectedBlocks(
        closestUnderlyingBlock,
        allColumnBlocks
      );

      const isClosestSingleBlock = closestBlockOverlapping.length === 1;

      if (isClosestSingleBlock) {
        const closestBlockUnderlying = findUnderlyingBlocks(
          closestUnderlyingBlock,
          allColumnBlocks
        );
        const closestBlockUnderlyingCount = closestBlockUnderlying.length;
        const currentBlockUnderlyingCount = underlyingBlocks.length;

        if (
          Math.abs(closestBlockUnderlyingCount - currentBlockUnderlyingCount) <=
          1
        ) {
          return LAYER_SEPARATION_BONUS;
        }
      }
    }
  }
  return 0;
}

const calculateSingleBlockWithUnderlyingLayout = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[],
  underlyingBlocks: ProgramBlock[]
): { width: string; left: string; zIndex: number; column: number } => {
  const blockStart = toMinutes(block.start);

  const underlyingOverlappingBlocks = underlyingBlocks.filter((otherBlock) => {
    const otherStart = toMinutes(otherBlock.start);
    return otherStart < blockStart && checkVisualOverlap(block, otherBlock);
  });

  const baseOffsetRatio = underlyingBlocks.length * BASE_OFFSET_RATIO;
  const additionalOffsetRatio =
    underlyingOverlappingBlocks.length * ADDITIONAL_OFFSET_RATIO;

  const layerSeparationBonus = getLayerSeparationBonus(
    block,
    allColumnBlocks,
    underlyingBlocks
  );

  const finalOffsetRatio =
    baseOffsetRatio + additionalOffsetRatio + layerSeparationBonus;
  const leftOffset = TOTAL_WIDTH_RATIO * finalOffsetRatio;
  const width = TOTAL_WIDTH_RATIO - leftOffset;

  const zIndex = calculateSimpleZIndex(block, allColumnBlocks);

  return {
    width: `${(width * 100).toFixed(1)}%`,
    left: `${(leftOffset * 100).toFixed(1)}%`,
    zIndex,
    column: 1,
  };
};

const calculateSingleBlockLayout = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[]
): { width: string; left: string; zIndex: number; column: number } => {
  const zIndex = calculateSimpleZIndex(block, allColumnBlocks);

  return {
    width: `${TOTAL_WIDTH_RATIO * 100}%`,
    left: '0%',
    zIndex,
    column: 0,
  };
};

const sortOverlappingBlocks = (blocks: ProgramBlock[]): ProgramBlock[] => {
  return blocks.sort((a, b) => {
    const startDiff = toMinutes(a.start) - toMinutes(b.start);
    if (startDiff !== 0) return startDiff;

    const durationA = toMinutes(a.end) - toMinutes(a.start);
    const durationB = toMinutes(b.end) - toMinutes(b.start);
    const durationDiff = durationB - durationA;
    if (durationDiff !== 0) return durationDiff;

    return a.id.localeCompare(b.id);
  });
};

const findGroupUnderlyingBlocks = (
  sortedBlocks: ProgramBlock[],
  allColumnBlocks: ProgramBlock[]
): ProgramBlock[] => {
  const groupBlockIds = new Set(sortedBlocks.map((b) => b.id));
  return allColumnBlocks.filter((otherBlock) => {
    if (groupBlockIds.has(otherBlock.id)) return false;
    return sortedBlocks.some(
      (groupBlock) =>
        toMinutes(otherBlock.start) < toMinutes(groupBlock.start) &&
        checkVisualOverlap(groupBlock, otherBlock)
    );
  });
};

const calculateMultiBlockDimensions = (
  blockIndex: number,
  totalOverlapping: number,
  baseOffset: number
): { width: number; leftOffset: number } => {
  const config = MULTI_BLOCK_LAYOUT_CONFIG[totalOverlapping];

  if (config && config[blockIndex]) {
    const { width, offset } = config[blockIndex];
    return {
      width: TOTAL_WIDTH_RATIO * width,
      leftOffset: TOTAL_WIDTH_RATIO * offset + baseOffset,
    };
  }

  const baseWidth = TOTAL_WIDTH_RATIO * MULTI_BLOCK_FALLBACK_BASE_WIDTH_RATIO;
  let finalWidth: number;
  let leftOffset: number;

  if (blockIndex === 0) {
    leftOffset = baseOffset;
    finalWidth = baseWidth;
  } else if (blockIndex === totalOverlapping - 1) {
    const calculatedOffset =
      TOTAL_WIDTH_RATIO *
      (MULTI_BLOCK_FALLBACK_OFFSET_START +
        (blockIndex - 1) * MULTI_BLOCK_FALLBACK_OFFSET_STEP);
    leftOffset = calculatedOffset + baseOffset;
    finalWidth = TOTAL_WIDTH_RATIO - leftOffset;
  } else {
    const calculatedOffset =
      TOTAL_WIDTH_RATIO *
      (MULTI_BLOCK_FALLBACK_OFFSET_START +
        (blockIndex - 1) * MULTI_BLOCK_FALLBACK_OFFSET_STEP);
    leftOffset = calculatedOffset + baseOffset;
    finalWidth = baseWidth;
  }

  return { width: finalWidth, leftOffset };
};

const calculateMultiBlockLayout = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[],
  overlappingBlocks: ProgramBlock[]
): { width: string; left: string; zIndex: number; column: number } => {
  const sortedBlocks = sortOverlappingBlocks(overlappingBlocks);
  const blockIndex = sortedBlocks.findIndex((b) => b.id === block.id);
  const totalOverlapping = sortedBlocks.length;
  const groupUnderlyingBlocks = findGroupUnderlyingBlocks(
    sortedBlocks,
    allColumnBlocks
  );
  const baseOffsetRatio =
    groupUnderlyingBlocks.length * GROUP_UNDERLYING_BLOCK_OFFSET_RATIO;
  const baseOffset = TOTAL_WIDTH_RATIO * baseOffsetRatio;
  const allOffsets = sortedBlocks.map((_b, i) =>
    calculateMultiBlockDimensions(i, totalOverlapping, baseOffset)
  );
  const maxRight = Math.max(...allOffsets.map((o) => o.leftOffset + o.width));
  let compressRatio = 1;
  if (maxRight > TOTAL_WIDTH_RATIO) {
    compressRatio = TOTAL_WIDTH_RATIO / maxRight;
  }
  const { width: finalWidth, leftOffset } = allOffsets[blockIndex];
  const adjustedLeft = leftOffset * compressRatio;
  const adjustedWidth = finalWidth * compressRatio;
  const constrainedWidth = Math.max(adjustedWidth, MIN_WIDTH);
  const zIndex = calculateSimpleZIndex(block, allColumnBlocks);
  return {
    width: `${(constrainedWidth * 100).toFixed(1)}%`,
    left: `${(adjustedLeft * 100).toFixed(1)}%`,
    zIndex,
    column: blockIndex,
  };
};

const calculateColumnScheduleLayout = (
  currentBlock: ProgramBlock,
  allBlocks: ProgramBlock[]
): { width: string; left: string; zIndex: number; column: number } => {
  const visuallyOverlappingBlocks = allBlocks.filter((block) => {
    if (block.id === currentBlock.id) return false;

    return checkColumnScheduleVisualOverlap(currentBlock, block);
  });

  if (visuallyOverlappingBlocks.length === 0) {
    return { width: '100%', left: '0', zIndex: 1, column: 0 };
  }

  const allOverlappingBlocks = [currentBlock, ...visuallyOverlappingBlocks];

  allOverlappingBlocks.sort((a, b) => {
    const aStart = toMinutes(a.start);
    const bStart = toMinutes(b.start);

    if (aStart !== bStart) {
      return aStart - bStart;
    }

    const aDuration = toMinutes(a.end) - toMinutes(a.start);
    const bDuration = toMinutes(b.end) - toMinutes(b.start);

    return bDuration - aDuration;
  });

  const totalBlocks = allOverlappingBlocks.length;
  const currentIndex = allOverlappingBlocks.findIndex(
    (block) => block.id === currentBlock.id
  );

  const width = `${100 / totalBlocks}%`;
  const left = `${(currentIndex * 100) / totalBlocks}%`;

  const zIndex = currentIndex + 1;

  return { width, left, zIndex, column: currentIndex };
};

const checkColumnScheduleVisualOverlap = (
  block1: ProgramBlock,
  block2: ProgramBlock,
  minHeightInMinutes = 30
): boolean => {
  const start1 = toMinutes(block1.start);
  const end1 = toMinutes(block1.end);
  const start2 = toMinutes(block2.start);
  const end2 = toMinutes(block2.end);

  const duration1 = end1 - start1;
  const duration2 = end2 - start2;

  const visualEnd1 =
    duration1 < minHeightInMinutes ? start1 + minHeightInMinutes : end1;
  const visualEnd2 =
    duration2 < minHeightInMinutes ? start2 + minHeightInMinutes : end2;

  return start1 < visualEnd2 && start2 < visualEnd1;
};

export const calculateBlockLayout = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[] = [],
  scheduleType?: 'columnSchedule' | 'weekSchedule'
): { width: string; left: string; zIndex: number; column: number } => {
  if (scheduleType === 'columnSchedule') {
    return calculateColumnScheduleLayout(block, allColumnBlocks);
  }

  const { overlappingBlocks, underlyingBlocks } = findRelatedBlocks(
    block,
    allColumnBlocks
  );

  return determineLayoutStrategy(
    block,
    allColumnBlocks,
    overlappingBlocks,
    underlyingBlocks
  );
};

const findRelatedBlocks = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[]
) => {
  const overlappingBlocks = findAllConnectedBlocks(block, allColumnBlocks);
  const underlyingBlocks = findUnderlyingBlocks(block, allColumnBlocks);

  return { overlappingBlocks, underlyingBlocks };
};

const determineLayoutStrategy = (
  block: ProgramBlock,
  allColumnBlocks: ProgramBlock[],
  overlappingBlocks: ProgramBlock[],
  underlyingBlocks: ProgramBlock[]
) => {
  if (overlappingBlocks.length === 1 && underlyingBlocks.length > 0) {
    return calculateSingleBlockWithUnderlyingLayout(
      block,
      allColumnBlocks,
      underlyingBlocks
    );
  }

  if (overlappingBlocks.length === 1) {
    return calculateSingleBlockLayout(block, allColumnBlocks);
  }

  return calculateMultiBlockLayout(block, allColumnBlocks, overlappingBlocks);
};

export const checkColumnScheduleTimeOverlap = (
  newStart: string,
  newEnd: string,
  existingBlocks: ProgramBlock[],
  excludeBlockId?: string
): boolean => {
  const newStartMinutes = toMinutes(newStart);
  const newEndMinutes = toMinutes(newEnd);

  return existingBlocks.some((block) => {
    if (excludeBlockId && block.id === excludeBlockId) return false;

    const blockStart = toMinutes(block.start);
    const blockEnd = toMinutes(block.end);

    return newStartMinutes < blockEnd && blockStart < newEndMinutes;
  });
};

export const getBlockContentLayout = (block: ProgramBlock) => {
  const duration = toMinutes(block.end) - toMinutes(block.start);
  const isShortBlock = duration <= 30;
  const isLongEvent = duration > 60;

  return {
    isShortBlock,
    allowTitleWrap: !isShortBlock && isLongEvent,
    titleClass: !isShortBlock && isLongEvent ? 'multiLine' : 'singleLine',
    blockClass: isShortBlock ? 'shortBlock' : 'normalBlock',
  };
};
